/**
 * Eater of Worlds — the first big boss: a cosmic segmented serpent.
 *
 * Structure: [ HEAD, body × N, TAIL ] where N is random in [WORM.minSegments,
 * WORM.maxSegments]. Each segment is an independently-damageable node (satisfies the
 * turret/cannon `Damageable` shape), so the player's whole arsenal targets any segment.
 *
 * Movement — FOLLOW-THE-LEADER: the head ROAMS with a weaving slither (only turning to
 * aim at the player while its beam fires) and records a path trail; every following
 * segment samples that trail at its own slot distance behind the head, so the body
 * snakes along exactly where the head went. A destroyed segment stays IN the chain as
 * burning wreckage — it keeps riding along but stops firing, so the worm stays continuous.
 *
 * Weapons:
 *   • HEAD — a charged forward BEAM on a long cooldown (charge → fire ~2s → cooldown).
 *     The beam carves asteroids and moons, and damages other hostiles AND the player.
 *   • BODY — every body segment aims at the player and fires its own bolt on its own
 *     cooldown.
 *
 * The head is always damageable but is CLAMPED at ≥ WORM.headFloorFrac of its max HP
 * until every body/tail segment is destroyed — only then can it be finished off.
 */
import { Vector2 } from "@/math/Vector2";
import { WORM } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";
import type { Damageable } from "@/game/combat/turrets";

export type WormPart = "head" | "body" | "tail";

export interface WormSegment {
  kind: WormPart;
  pos: Vector2;
  vel: Vector2; // world velocity (for turret/cannon lead-aim)
  heading: number; // forward = toward the segment ahead (toward the head)
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  dead: boolean;
  deathHandled: boolean;
  cooldown: number; // body-bolt cooldown
  fireSpots: { lat: number; fwd: number }[]; // wreckage fire offsets (local, world units)
}

export interface WormBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export interface WormBeam {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface WormCtx {
  playerX: number;
  playerY: number;
  playerRadius: number;
  playerAlive: boolean;
  bodies: MineableBody[]; // asteroids + moons — the beam carves these
  foes: Damageable[]; // OTHER hostiles (never the worm) — the beam damages these too
  onHitPlayer: (dmg: number, x: number, y: number) => void;
  onBeamKillBody: (body: MineableBody) => void; // an asteroid/moon fully carved by the beam
  onSegmentDeath: (x: number, y: number, big: boolean) => void; // wreck VFX
  onDefeated: (x: number, y: number) => void; // head killed → boss defeated (rewards + blast)
  onBeamSpark: (x: number, y: number) => void; // impact sparks along the beam
}

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/** Shortest distance from point (px,py) to segment (ax,ay)-(bx,by). */
function distToSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy || 1e-6;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + dx * t;
  const cy = ay + dy * t;
  return Math.hypot(px - cx, py - cy);
}

function randomWaypoint(): Vector2 {
  const a = Math.random() * Math.PI * 2;
  const d = 300 + Math.random() * 1500;
  return new Vector2(Math.cos(a) * d, Math.sin(a) * d);
}

const FOE_HIT_RADIUS = 34; // generous half-width for beam-vs-ship tests

export class WormManager {
  readonly segments: WormSegment[] = [];
  readonly bullets: WormBullet[] = [];
  private trail: { x: number; y: number }[] = [];
  private age = 0;
  private roam: Vector2 = randomWaypoint(); // head's wander target (it roams, not chases)
  active = true; // fighting; set false once defeated (or paused during emergence)
  defeated = false;
  emerging = false; // climbing out of the planet — weapons held, head moves straight out
  private emergeTimer = 0;
  // Charged beam
  beamPhase: "idle" | "charge" | "fire" = "idle";
  beamTimer = WORM.beamCooldown * 0.6; // first blast comes a bit sooner
  chargeFrac = 0; // 0..1 while charging (drives the telegraph glow)
  beam: WormBeam | null = null;

  constructor(
    spawn: { x: number; y: number },
    heading: number,
    bodyCount: number,
    emerge = false,
  ) {
    const fwd = { x: Math.cos(heading), y: Math.sin(heading) };
    const total = bodyCount + 2; // + head + tail
    if (emerge) {
      // COILED inside the planet: the trail is a single point, so every segment starts
      // stacked at the centre and unwraps one-by-one as the head climbs out.
      this.trail.push({ x: spawn.x, y: spawn.y });
      this.trail.push({ x: spawn.x, y: spawn.y });
      this.emerging = true;
      this.emergeTimer = WORM.emergeTime;
    } else {
      // Seed the trail as a straight line stretching BEHIND the head (head at the end).
      const seedLen = (total + 3) * WORM.spacing;
      const steps = Math.ceil(seedLen / WORM.trailStep);
      for (let s = steps; s >= 0; s--) {
        this.trail.push({
          x: spawn.x - fwd.x * (s * WORM.trailStep),
          y: spawn.y - fwd.y * (s * WORM.trailStep),
        });
      }
    }
    // Build the chain: head, bodies, tail.
    for (let i = 0; i < total; i++) {
      const kind: WormPart = i === 0 ? "head" : i === total - 1 ? "tail" : "body";
      const maxHp =
        kind === "head" ? WORM.headMaxHp : kind === "tail" ? WORM.tailMaxHp : WORM.bodyMaxHp;
      const sample = this.sampleTrail(i * WORM.spacing);
      this.segments.push({
        kind,
        pos: new Vector2(sample.x, sample.y),
        vel: new Vector2(),
        heading: sample.heading || heading,
        hp: maxHp,
        maxHp,
        shield: 0,
        maxShield: 0,
        shieldFlash: 0,
        hitTimer: 999,
        dead: false,
        deathHandled: false,
        cooldown: Math.random() * WORM.fireInterval,
        fireSpots: [],
      });
    }
  }

  get head(): WormSegment {
    return this.segments[0]!;
  }
  /** Live segments only — what the player's weapons should be able to target. */
  get liveSegments(): WormSegment[] {
    return this.segments.filter((s) => !s.dead && s.hp > 0);
  }

  /** Sample a point `distBack` world-units back along the head's path (head at trail end). */
  private sampleTrail(distBack: number): { x: number; y: number; heading: number } {
    const t = this.trail;
    const n = t.length;
    if (n === 0) return { x: 0, y: 0, heading: 0 };
    if (n === 1) return { x: t[0]!.x, y: t[0]!.y, heading: 0 };
    let acc = 0;
    for (let i = n - 1; i > 0; i--) {
      const a = t[i]!;
      const b = t[i - 1]!;
      const seg = Math.hypot(a.x - b.x, a.y - b.y) || 1e-6;
      if (acc + seg >= distBack) {
        const f = (distBack - acc) / seg;
        return {
          x: a.x + (b.x - a.x) * f,
          y: a.y + (b.y - a.y) * f,
          heading: Math.atan2(a.y - b.y, a.x - b.x), // toward the newer point = toward the head
        };
      }
      acc += seg;
    }
    const o = t[0]!;
    const o1 = t[1]!;
    return { x: o.x, y: o.y, heading: Math.atan2(o1.y - o.y, o1.x - o.x) };
  }

  private pushTrail(x: number, y: number): void {
    const t = this.trail;
    const last = t[t.length - 1];
    if (!last || (last.x - x) ** 2 + (last.y - y) ** 2 >= WORM.trailStep * WORM.trailStep) {
      t.push({ x, y });
    }
    // Trim old points so the stored path only just covers the whole worm.
    const maxLen = (this.segments.length + 3) * WORM.spacing;
    let acc = 0;
    let cut = 0;
    for (let i = t.length - 1; i > 0; i--) {
      acc += Math.hypot(t[i]!.x - t[i - 1]!.x, t[i]!.y - t[i - 1]!.y);
      if (acc > maxLen) {
        cut = i - 1;
        break;
      }
    }
    if (cut > 0) t.splice(0, cut);
  }

  update(dt: number, ctx: WormCtx): void {
    if (!this.active) return;
    const head = this.head;
    if (this.emerging) {
      this.emergeTimer -= dt;
      if (this.emergeTimer <= 0) this.emerging = false;
    }

    // 1) Head ROAMS the map (weaving); it only turns to AIM at the player while the
    //    beam is charging/firing. While EMERGING it climbs straight out of the planet
    //    (a bit faster) so the body unwraps behind it. Body follows the trail.
    if (!head.dead) {
      this.age += dt;
      const aiming = this.beamPhase === "fire"; // aim only once the beam is OUT, not while charging
      let desired: number;
      if (this.emerging) {
        desired = head.heading; // climb straight out of the planet
      } else if (aiming) {
        desired = Math.atan2(ctx.playerY - head.pos.y, ctx.playerX - head.pos.x);
      } else {
        if ((this.roam.x - head.pos.x) ** 2 + (this.roam.y - head.pos.y) ** 2 < 220 * 220) {
          this.roam = randomWaypoint();
        }
        const toRoam = Math.atan2(this.roam.y - head.pos.y, this.roam.x - head.pos.x);
        desired = toRoam + Math.sin(this.age * WORM.weaveFreq) * WORM.weaveAmp;
      }
      const diff = norm(desired - head.heading);
      const step = WORM.turnRate * dt;
      head.heading += diff > step ? step : diff < -step ? -step : diff;
      const spd = this.emerging ? WORM.speed * 2.4 : WORM.speed;
      head.vel.set(Math.cos(head.heading) * spd, Math.sin(head.heading) * spd);
      head.pos.x += head.vel.x * dt;
      head.pos.y += head.vel.y * dt;
      this.pushTrail(head.pos.x, head.pos.y);
      if (!this.emerging) this.updateBeam(dt, ctx);
    }

    // 2) Body/tail segments follow the head's path at their slot distance — dead ones
    //    stay in the chain too, so the worm stays continuous; wreckage just rides along.
    for (let i = 1; i < this.segments.length; i++) {
      const seg = this.segments[i]!;
      const s = this.sampleTrail(i * WORM.spacing);
      const px = seg.pos.x;
      const py = seg.pos.y;
      seg.pos.set(s.x, s.y);
      seg.heading = s.heading;
      seg.vel.set((s.x - px) / dt, (s.y - py) / dt);
    }

    // 3) Each living BODY segment fires its own bolt at the NEAREST target — the player
    //    OR any other hostile (the Eater is on nobody's side). Not while still emerging.
    for (let i = 1; i < this.segments.length && !this.emerging; i++) {
      const seg = this.segments[i]!;
      if (seg.dead || seg.kind !== "body") continue;
      seg.cooldown -= dt;
      if (seg.cooldown > 0) continue;
      let tx = 0;
      let ty = 0;
      let bestD = Infinity;
      if (ctx.playerAlive) {
        tx = ctx.playerX;
        ty = ctx.playerY;
        bestD = (tx - seg.pos.x) ** 2 + (ty - seg.pos.y) ** 2;
      }
      for (const f of ctx.foes) {
        if (f.hp <= 0) continue;
        const d = (f.pos.x - seg.pos.x) ** 2 + (f.pos.y - seg.pos.y) ** 2;
        if (d < bestD) {
          bestD = d;
          tx = f.pos.x;
          ty = f.pos.y;
        }
      }
      if (bestD === Infinity) continue; // nothing to shoot at
      const ang = Math.atan2(ty - seg.pos.y, tx - seg.pos.x);
      const ax = Math.cos(ang);
      const ay = Math.sin(ang);
      this.bullets.push({
        x: seg.pos.x + ax * WORM.muzzleLen,
        y: seg.pos.y + ay * WORM.muzzleLen,
        vx: ax * WORM.boltSpeed,
        vy: ay * WORM.boltSpeed,
        life: WORM.boltLife,
      });
      seg.cooldown = WORM.fireInterval * (0.8 + Math.random() * 0.4);
    }

    // 4) Flash/hit decay + register fresh deaths.
    for (const seg of this.segments) {
      seg.shieldFlash = Math.max(0, seg.shieldFlash - dt * 3);
      seg.hitTimer += dt;
      if (seg.hp <= 0 && !seg.dead && seg.kind !== "head") this.killSegment(seg, ctx);
    }

    // 5) Head HP floor: can't drop below headFloorFrac until all body/tail segments die.
    const bodiesAlive = this.segments.some((s) => s.kind !== "head" && !s.dead);
    if (bodiesAlive && !head.dead) {
      head.hp = Math.max(head.hp, WORM.headFloorFrac * head.maxHp);
    } else if (!bodiesAlive && head.hp <= 0 && !this.defeated) {
      this.defeat(ctx);
    }

    // 6) Advance body bolts — they hit the player OR any other hostile in the way
    //    (friendly fire), but NEVER the worm itself (segments aren't tested).
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      if (ctx.playerAlive) {
        const rr = ctx.playerRadius + 5;
        if ((ctx.playerX - b.x) ** 2 + (ctx.playerY - b.y) ** 2 < rr * rr) {
          ctx.onHitPlayer(WORM.boltDamage, b.x, b.y);
          hit = true;
        }
      }
      if (!hit) {
        for (const f of ctx.foes) {
          if (f.hp <= 0) continue;
          const rr = FOE_HIT_RADIUS + 4;
          if ((f.pos.x - b.x) ** 2 + (f.pos.y - b.y) ** 2 < rr * rr) {
            this.hurt(f, WORM.boltDamage);
            ctx.onBeamSpark(b.x, b.y);
            hit = true;
            break;
          }
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  private updateBeam(dt: number, ctx: WormCtx): void {
    this.beamTimer -= dt;
    if (this.beamPhase === "idle") {
      this.beam = null;
      this.chargeFrac = 0;
      if (this.beamTimer <= 0) {
        this.beamPhase = "charge";
        this.beamTimer = WORM.beamCharge;
      }
    } else if (this.beamPhase === "charge") {
      this.chargeFrac = Math.max(0, Math.min(1, 1 - this.beamTimer / WORM.beamCharge));
      if (this.beamTimer <= 0) {
        this.beamPhase = "fire";
        this.beamTimer = WORM.beamDuration;
      }
    } else {
      this.fireBeam(dt, ctx);
      if (this.beamTimer <= 0) {
        this.beamPhase = "idle";
        this.beamTimer = WORM.beamCooldown;
        this.beam = null;
        this.chargeFrac = 0;
      }
    }
  }

  /** The beam sweeps forward from the head's maw, carving bodies and burning ships. */
  private fireBeam(dt: number, ctx: WormCtx): void {
    const head = this.head;
    const dx = Math.cos(head.heading);
    const dy = Math.sin(head.heading);
    const mx = head.pos.x + dx * (WORM.headWorldHeight * 0.5);
    const my = head.pos.y + dy * (WORM.headWorldHeight * 0.5);
    const ex = mx + dx * WORM.beamLength;
    const ey = my + dy * WORM.beamLength;
    this.beam = { x0: mx, y0: my, x1: ex, y1: ey };
    const hw = WORM.beamWidth;

    if (
      ctx.playerAlive &&
      distToSeg(ctx.playerX, ctx.playerY, mx, my, ex, ey) < hw + ctx.playerRadius
    ) {
      ctx.onHitPlayer(WORM.beamPlayerDps * dt, ctx.playerX, ctx.playerY);
    }
    for (const f of ctx.foes) {
      if (f.hp <= 0) continue;
      if (distToSeg(f.pos.x, f.pos.y, mx, my, ex, ey) < hw + FOE_HIT_RADIUS) {
        this.hurt(f, WORM.beamEnemyDps * dt);
        if (Math.random() < 0.3) ctx.onBeamSpark(f.pos.x, f.pos.y);
      }
    }
    for (const b of ctx.bodies) {
      if (b.ore <= 0) continue;
      if (distToSeg(b.pos.x, b.pos.y, mx, my, ex, ey) < hw + b.radius) {
        b.ore -= WORM.beamBodyDps * dt;
        if (Math.random() < 0.25) ctx.onBeamSpark(b.pos.x, b.pos.y);
        if (b.ore <= 0) {
          b.ore = 0;
          ctx.onBeamKillBody(b);
        }
      }
    }
  }

  /** Apply shield-then-hull damage to any Damageable (used by the beam + body bolts). */
  private hurt(t: Damageable, amount: number): void {
    let dmg = amount;
    if (t.shield > 0) {
      const a = Math.min(t.shield, dmg);
      t.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) t.hp = Math.max(0, t.hp - dmg);
    t.shieldFlash = 1;
    t.hitTimer = 0;
  }

  private killSegment(seg: WormSegment, ctx: WormCtx): void {
    seg.dead = true;
    seg.deathHandled = true;
    seg.hp = 0;
    // 2..3 fires at random spots across the segment (local offsets, world units).
    const n = WORM.fireCountMin + Math.floor(Math.random() * (WORM.fireCountMax - WORM.fireCountMin + 1));
    seg.fireSpots = [];
    const r = WORM.collisionRadius * 0.7;
    for (let i = 0; i < n; i++) {
      seg.fireSpots.push({
        lat: (Math.random() * 2 - 1) * r,
        fwd: (Math.random() * 2 - 1) * r,
      });
    }
    ctx.onSegmentDeath(seg.pos.x, seg.pos.y, false);
  }

  private defeat(ctx: WormCtx): void {
    this.defeated = true;
    this.active = false;
    const head = this.head;
    head.dead = true;
    head.hp = 0;
    // Everything left standing goes up as wreckage.
    for (const seg of this.segments) {
      if (!seg.dead) {
        seg.dead = true;
        seg.hp = 0;
        const n =
          WORM.fireCountMin + Math.floor(Math.random() * (WORM.fireCountMax - WORM.fireCountMin + 1));
        seg.fireSpots = [];
        const r = WORM.collisionRadius * 0.7;
        for (let i = 0; i < n; i++) {
          seg.fireSpots.push({ lat: (Math.random() * 2 - 1) * r, fwd: (Math.random() * 2 - 1) * r });
        }
      }
    }
    ctx.onDefeated(head.pos.x, head.pos.y);
  }
}
